import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function Login() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice] = useState(() => { const r = sessionStorage.getItem("tt_logout_reason"); sessionStorage.removeItem("tt_logout_reason"); return r; });

  useEffect(() => { if (user) navigate("/tickets", { replace: true }); }, [user, navigate]);

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/request-code", { email });
      setStep("code");
      toast.success("If this email has an account, a sign-in code is on its way. Check your inbox.");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const verify = async (value) => {
    const c = value ?? code;
    if (c.length !== 6) return;
    setBusy(true); setError("");
    try {
      await api.post("/auth/verify-code", { email, code: c });
      await refresh();
      navigate("/tickets", { replace: true });
    } catch (err) { setError(errMsg(err)); setCode(""); } finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-full lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#090A0D] text-slate-100 lg:block">
        <div className="grid-bg absolute inset-0 opacity-40" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-2">
            <span className="grid h-7 w-7 place-items-center bg-blue-500 text-xs font-black text-white mono">TT</span>
            <span className="mono text-xs font-semibold uppercase tracking-[0.25em]">Total Tickets</span>
          </div>
          <div className="max-w-lg">
            <div className="eyebrow mb-3 text-blue-300">Email-to-ticket operations console</div>
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">Every email becomes a ticket. Every ticket is tracked to the second.</h1>
            <p className="mt-5 text-sm leading-relaxed text-slate-400">Unique inbound addresses per channel, weighted or round-robin routing, inactivity reassignment, rotting alerts, closure approvals, scheduled reports and nightly snapshots. Nothing is ever deleted.</p>
          </div>
          <div className="grid grid-cols-3 gap-px bg-slate-800 text-xs">
            {[["Routing", "Weighted / round robin"], ["History", "Full compliance trail"], ["Restore", "Any snapshot, any ticket"]].map(([k, v]) => (
              <div key={k} className="bg-[#0E1117] p-4"><div className="eyebrow text-slate-500">{k}</div><div className="mt-1 font-semibold text-slate-200">{v}</div></div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <span className="grid h-7 w-7 place-items-center bg-primary text-xs font-black text-primary-foreground mono">TT</span>
            <span className="mono text-xs font-semibold uppercase tracking-[0.25em]">Total Tickets</span>
          </div>
          {notice && <div className="mb-4 border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs" data-testid="logout-notice">{notice}</div>}
          {step === "email" ? (
            <form onSubmit={requestCode} className="space-y-5" data-testid="login-email-form">
              <div>
                <div className="eyebrow mb-1">Sign in</div>
                <h2 className="text-2xl font-bold tracking-tight">Enter your work email</h2>
                <p className="mt-1 text-sm text-muted-foreground">We'll email you a 6-digit code. No passwords.</p>
              </div>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="h-11 pl-9 mono text-sm" data-testid="login-email-input" />
              </div>
              {error && <div className="border border-red-500/50 bg-red-500/10 px-3 py-2 text-xs text-red-500" data-testid="login-error">{error}</div>}
              <Button type="submit" className="h-11 w-full" disabled={busy} data-testid="login-request-code-btn">
                {busy ? "Sending…" : "Email me a code"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
          ) : (
            <div className="space-y-5" data-testid="login-code-form">
              <div>
                <div className="eyebrow mb-1">Check your inbox</div>
                <h2 className="text-2xl font-bold tracking-tight">Enter the 6-digit code</h2>
                <p className="mt-1 text-sm text-muted-foreground">Sent to <span className="mono">{email}</span>. Expires in 10 minutes.</p>
              </div>
              <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }} autoFocus data-testid="login-code-input">
                <InputOTPGroup className="gap-2">
                  {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 rounded-none border mono text-lg" />)}
                </InputOTPGroup>
              </InputOTP>
              {error && <div className="border border-red-500/50 bg-red-500/10 px-3 py-2 text-xs text-red-500" data-testid="login-error">{error}</div>}
              <Button className="h-11 w-full" disabled={busy || code.length !== 6} onClick={() => verify()} data-testid="login-verify-btn">
                <KeyRound className="mr-2 h-4 w-4" /> {busy ? "Verifying…" : "Sign in"}
              </Button>
              <button className="text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={() => { setStep("email"); setCode(""); setError(""); }} data-testid="login-change-email-btn">Use a different email</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
